import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Sparkles,
  CheckCircle2,
  Target,
  Trophy,
  Brain,
  ChevronRight,
  X,
  AlertCircle,
  ListChecks,
} from 'lucide-react';
import API from './api';
import MyVisitsToday from './MyVisitsToday';

const DISMISS_KEY_PREFIX = 'teacher_ai_assistant_dismissed_';

function todayKey() {
  const d = new Date();
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  return `${yyyy}-${mm}-${dd}`;
}

function getDismissKey() {
  return `${DISMISS_KEY_PREFIX}${todayKey()}`;
}

function safeLocalStorageGet(key) {
  try {
    return localStorage.getItem(key);
  } catch (e) {
    return null;
  }
}

function safeLocalStorageSet(key, value) {
  try {
    localStorage.setItem(key, value);
  } catch (e) {
    // ignore
  }
}

function pluralize(count, singular, plural = null) {
  if (count === 1) return singular;
  return plural || `${singular}s`;
}

function SummaryLine({ icon, title, value, accent = 'sky' }) {
  const accentMap = {
    sky: 'bg-sky-50 text-sky-700 border-sky-200',
    amber: 'bg-amber-50 text-amber-700 border-amber-200',
    emerald: 'bg-emerald-50 text-emerald-700 border-emerald-200',
    rose: 'bg-rose-50 text-rose-700 border-rose-200',
    indigo: 'bg-indigo-50 text-indigo-700 border-indigo-200',
  };

  return (
    <div className={`rounded-2xl border px-3 py-3 ${accentMap[accent] || accentMap.sky}`}>
      <div className="flex items-start gap-3">
        <div className="shrink-0 mt-0.5">{icon}</div>
        <div className="min-w-0">
          <div className="text-xs font-semibold uppercase tracking-wide opacity-80">
            {title}
          </div>
          <div className="text-sm font-medium mt-1 leading-5">{value}</div>
        </div>
      </div>
    </div>
  );
}

function StudentPreview({ title, items = [], accent = 'sky' }) {
  if (!items.length) return null;

  const accentMap = {
    sky: 'border-sky-200 bg-sky-50',
    amber: 'border-amber-200 bg-amber-50',
    rose: 'border-rose-200 bg-rose-50',
    indigo: 'border-indigo-200 bg-indigo-50',
  };

  const preview = items.slice(0, 5);
  const extra = items.length - preview.length;

  return (
    <div className={`rounded-2xl border p-3 ${accentMap[accent] || accentMap.sky}`}>
      <div className="text-xs font-semibold text-slate-700 mb-2">{title}</div>
      <div className="flex flex-wrap gap-2">
        {preview.map((s) => (
          <span
            key={s.id}
            className="inline-flex items-center rounded-full border border-white/80 bg-white px-2.5 py-1 text-xs text-slate-700 shadow-sm"
          >
            {s.name}
          </span>
        ))}
        {extra > 0 && (
          <span className="inline-flex items-center rounded-full border border-white/80 bg-white px-2.5 py-1 text-xs text-slate-500 shadow-sm">
            +{extra} more
          </span>
        )}
      </div>
    </div>
  );
}

function SectionList({ title, items = [], tone = 'sky' }) {
  const toneMap = {
    sky: {
      wrap: 'border-sky-200 bg-sky-50',
      badge: 'bg-sky-100 text-sky-700 border-sky-200',
    },
    amber: {
      wrap: 'border-amber-200 bg-amber-50',
      badge: 'bg-amber-100 text-amber-700 border-amber-200',
    },
  };

  const ui = toneMap[tone] || toneMap.sky;

  return (
    <div className={`rounded-2xl border p-3 ${ui.wrap}`}>
      <div className="flex items-center justify-between gap-3 mb-3">
        <div className="text-sm font-semibold text-slate-800">{title}</div>
        <span className={`inline-flex items-center rounded-full border px-2 py-0.5 text-[11px] font-medium ${ui.badge}`}>
          {items.length}
        </span>
      </div>

      {items.length === 0 ? (
        <div className="text-sm text-slate-500">No students in this list.</div>
      ) : (
        <div className="max-h-[260px] overflow-y-auto space-y-2 pr-1">
          {items.map((s, idx) => (
            <div
              key={s.id}
              className="flex items-center justify-between gap-3 rounded-xl border border-white/80 bg-white px-3 py-2"
            >
              <div className="min-w-0">
                <div className="text-sm font-medium text-slate-800 truncate">
                  {s.name}
                </div>
                <div className="text-[11px] text-slate-500">
                  Student ID: {s.id}
                </div>
              </div>
              <div className="shrink-0 text-[11px] text-slate-400">
                #{idx + 1}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function AIAssistantModal({ open, summary, onClose }) {
  if (!open || !summary?.show) return null;

  const {
    variant,
    current_month_label,
    goals_month_label,
    achievements_month_label,
    missing_goals_students = [],
    missing_achievements_students = [],
  } = summary;

  const goalsLabel = goals_month_label || current_month_label || 'this month';
  const achievementsLabel = achievements_month_label || current_month_label || 'this month';

  return (
    <div className="fixed inset-0 z-[80]">
      <div className="absolute inset-0 bg-black/45" onClick={onClose} />
      <div className="absolute inset-x-0 top-0 bottom-0 md:inset-0 flex items-end md:items-center justify-center p-0 md:p-4">
        <div className="w-full md:max-w-3xl bg-white md:rounded-3xl shadow-2xl border border-slate-200 max-h-[92vh] overflow-hidden">
          <div className="px-4 md:px-5 py-4 border-b border-slate-200 flex items-start justify-between gap-3">
            <div className="min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <div className="rounded-xl bg-indigo-600 p-2 text-white">
                  <Sparkles size={18} />
                </div>
                <h3 className="text-base md:text-lg font-semibold text-slate-900">
                  AI Assistant details
                </h3>
              </div>
              <p className="mt-2 text-sm text-slate-600 leading-6">
                Review students who still need monthly documentation updates.
              </p>
            </div>

            <button
              type="button"
              onClick={onClose}
              className="inline-flex items-center justify-center rounded-full p-2 text-slate-500 hover:bg-slate-100 hover:text-slate-800"
              aria-label="Close modal"
              title="Close"
            >
              <X size={18} />
            </button>
          </div>

          <div className="p-4 md:p-5 overflow-y-auto max-h-[calc(92vh-80px)] space-y-4">
            {(variant === 'goals' || variant === 'both') && (
              <SectionList
                title={`Missing Goals — ${goalsLabel}`}
                items={missing_goals_students}
                tone="sky"
              />
            )}

            {(variant === 'achievements' || variant === 'both') && (
              <SectionList
                title={`Missing Achievements — ${achievementsLabel}`}
                items={missing_achievements_students}
                tone="amber"
              />
            )}

            <div className="rounded-2xl border border-indigo-200 bg-indigo-50 p-4">
              <div className="flex items-start gap-3">
                <Brain size={18} className="mt-0.5 shrink-0 text-indigo-700" />
                <div>
                  <div className="text-sm font-semibold text-indigo-800">
                    Skills reminder
                  </div>
                  <div className="text-sm text-indigo-700 mt-1 leading-6">
                    Skills are not validated in this reminder yet, but this is a good moment to review and update student Skills as well.
                  </div>
                </div>
              </div>
            </div>

            <div className="flex flex-col sm:flex-row gap-2 pt-1">
              <Link
                to="/teacher/achievement"
                onClick={onClose}
                className="inline-flex items-center justify-center gap-2 rounded-2xl bg-indigo-600 px-4 py-3 text-sm font-medium text-white shadow-sm hover:bg-indigo-700 transition"
              >
                Open Goals & Achievement
                <ChevronRight size={16} />
              </Link>

              <Link
                to="/teacher/students"
                onClick={onClose}
                className="inline-flex items-center justify-center gap-2 rounded-2xl border border-slate-300 bg-white px-4 py-3 text-sm font-medium text-slate-700 hover:bg-slate-50 transition"
              >
                Open Skills
                <ChevronRight size={16} />
              </Link>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function AIAssistantCard({ summary, onDismiss, onOpenDetails }) {
  const {
    variant,
    current_month_label,
    goals_month_label,
    achievements_month_label,
    missing_goals_count,
    missing_achievements_count,
    missing_goals_students,
    missing_achievements_students,
  } = summary || {};

  const goalsLabel = goals_month_label || current_month_label || 'this month';
  const achievementsLabel = achievements_month_label || current_month_label || 'this month';

  const headline = useMemo(() => {
    if (variant === 'both') return 'You still have a few important monthly updates to complete.';
    if (variant === 'goals') return 'Some monthly Goals are still missing.';
    if (variant === 'achievements') return 'Some monthly Achievements are still missing.';
    return 'Everything looks up to date.';
  }, [variant]);

  const helperText = useMemo(() => {
    if (variant === 'both') {
      return `Please complete Goals and Achievements for ${current_month_label || 'this month'}. Keeping both updated helps reports stay complete and accurate.`;
    }
    if (variant === 'goals') {
      return `Please complete Goals for ${goalsLabel}. This helps keep monthly planning and progress tracking aligned.`;
    }
    if (variant === 'achievements') {
      return `Please complete Achievements for ${achievementsLabel}. This helps keep student progress reports complete and accurate.`;
    }
    return '';
  }, [variant, current_month_label, goalsLabel, achievementsLabel]);

  if (!summary?.show) return null;

  return (
    <div className="mb-4 md:mb-5">
      <div className="relative overflow-hidden rounded-3xl border border-indigo-200 bg-gradient-to-br from-indigo-50 via-white to-sky-50 shadow-sm">
        <div className="absolute inset-y-0 right-0 w-32 bg-gradient-to-l from-indigo-100/40 to-transparent pointer-events-none" />

        <div className="relative p-4 md:p-5">
          <div className="flex items-start gap-3">
            <div className="shrink-0 rounded-xl md:rounded-2xl bg-indigo-600 p-2 md:p-2.5 text-white shadow-sm">
              <Sparkles size={16} className="md:w-4 md:h-4" />
            </div>

            <div className="min-w-0 flex-1">
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <div className="flex flex-wrap items-center gap-2">
                    <h2 className="text-base md:text-lg font-semibold text-slate-900">
                      AI Assistant
                    </h2>
                    <span className="inline-flex items-center rounded-full border border-indigo-200 bg-white px-2 py-0.5 text-[11px] font-medium text-indigo-700">
                      Daily reminder
                    </span>
                  </div>

                  <p className="mt-1 text-sm md:text-[15px] font-medium text-slate-800 leading-6">
                    {headline}
                  </p>

                  <p className="mt-2 text-sm text-slate-600 leading-6">
                    {helperText}
                  </p>
                </div>

                <button
                  type="button"
                  onClick={onDismiss}
                  className="shrink-0 inline-flex items-center justify-center rounded-full p-2 text-slate-500 hover:bg-white hover:text-slate-800 border border-transparent hover:border-slate-200"
                  title="Dismiss for today"
                  aria-label="Dismiss for today"
                >
                  <X size={18} />
                </button>
              </div>

              <div className="mt-4 grid grid-cols-1 gap-3">
                {(variant === 'goals' || variant === 'both') && (
                  <SummaryLine
                    icon={<Target size={18} />}
                    title="Goals"
                    value={`${missing_goals_count} ${pluralize(missing_goals_count, 'student')} still missing Goals for ${goalsLabel}.`}
                    accent="sky"
                  />
                )}

                {(variant === 'achievements' || variant === 'both') && (
                  <SummaryLine
                    icon={<Trophy size={18} />}
                    title="Achievements"
                    value={`${missing_achievements_count} ${pluralize(missing_achievements_count, 'student')} still missing Achievements for ${achievementsLabel}.`}
                    accent="amber"
                  />
                )}

                <SummaryLine
                  icon={<Brain size={18} />}
                  title="Skills reminder"
                  value="It would also be a good time to review and update Skills for your students."
                  accent="indigo"
                />
              </div>

              <div className="mt-4 grid grid-cols-1 gap-3">
                {(variant === 'goals' || variant === 'both') && (
                  <StudentPreview
                    title="Students still missing Goals"
                    items={missing_goals_students}
                    accent="sky"
                  />
                )}

                {(variant === 'achievements' || variant === 'both') && (
                  <StudentPreview
                    title="Students still missing Achievements"
                    items={missing_achievements_students}
                    accent="amber"
                  />
                )}
              </div>

              <div className="mt-4 flex flex-col sm:flex-row gap-2">
                <Link
                  to="/teacher/achievement"
                  className="inline-flex items-center justify-center gap-2 rounded-2xl bg-indigo-600 px-4 py-3 text-sm font-medium text-white shadow-sm hover:bg-indigo-700 transition"
                >
                  Open Goals & Achievement
                  <ChevronRight size={16} />
                </Link>

                <Link
                  to="/teacher/students"
                  className="inline-flex items-center justify-center gap-2 rounded-2xl border border-slate-300 bg-white px-4 py-3 text-sm font-medium text-slate-700 hover:bg-slate-50 transition"
                >
                  Update Skills
                  <ChevronRight size={16} />
                </Link>

                <button
                  type="button"
                  onClick={onOpenDetails}
                  className="inline-flex items-center justify-center gap-2 rounded-2xl border border-indigo-300 bg-white px-4 py-3 text-sm font-medium text-indigo-700 hover:bg-indigo-50 transition"
                >
                  View full list
                  <ListChecks size={16} />
                </button>
              </div>

              <div className="mt-3 flex items-start gap-2 rounded-2xl border border-slate-200 bg-white/70 px-3 py-2.5">
                <AlertCircle size={16} className="mt-0.5 shrink-0 text-slate-500" />
                <p className="text-xs md:text-sm text-slate-600 leading-5">
                  This reminder only checks your primary/default students and appears when monthly documentation is still missing.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

const TeacherHome = () => {
  const [summary, setSummary] = useState(null);
  const [loadingSummary, setLoadingSummary] = useState(true);
  const [dismissedToday, setDismissedToday] = useState(() => {
    return safeLocalStorageGet(getDismissKey()) === '1';
  });
  const [showDetailsModal, setShowDetailsModal] = useState(false);

  useEffect(() => {
    let mounted = true;

    const loadSummary = async () => {
      try {
        setLoadingSummary(true);
        const res = await API.get('/teacher/ai-assistant-summary');
        if (!mounted) return;
        setSummary(res.data || null);
      } catch (err) {
        console.error('Failed to load AI assistant summary:', err);
        if (!mounted) return;
        setSummary(null);
      } finally {
        if (mounted) setLoadingSummary(false);
      }
    };

    loadSummary();

    return () => {
      mounted = false;
    };
  }, []);

  useEffect(() => {
    setDismissedToday(safeLocalStorageGet(getDismissKey()) === '1');
  }, []);

  const handleDismiss = () => {
    safeLocalStorageSet(getDismissKey(), '1');
    setDismissedToday(true);
    setShowDetailsModal(false);
  };

  const shouldShowCard = useMemo(() => {
    return !!summary?.show && !dismissedToday;
  }, [summary, dismissedToday]);

  return (
    <div className="p-0 space-y-4 md:space-y-5">
      {!loadingSummary && shouldShowCard && (
        <AIAssistantCard
          summary={summary}
          onDismiss={handleDismiss}
          onOpenDetails={() => setShowDetailsModal(true)}
        />
      )}

      {!loadingSummary && summary?.show && dismissedToday && (
        <div className="rounded-2xl border border-slate-200 bg-white px-4 py-3 shadow-sm">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
            <div className="flex items-start gap-3">
              <div className="rounded-xl bg-emerald-50 p-2 text-emerald-700 border border-emerald-200">
                <CheckCircle2 size={18} />
              </div>
              <div>
                <div className="text-sm font-semibold text-slate-800">
                  AI Assistant reminder hidden for today
                </div>
                <div className="text-sm text-slate-600 mt-1">
                  You can still update Goals, Achievements, or Skills anytime.
                </div>
              </div>
            </div>

            <div className="flex flex-col sm:flex-row gap-2">
              <Link
                to="/teacher/achievement"
                className="inline-flex items-center justify-center rounded-xl border border-slate-300 bg-white px-3 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50"
              >
                Goals & Achievement
              </Link>
              <Link
                to="/teacher/students"
                className="inline-flex items-center justify-center rounded-xl border border-slate-300 bg-white px-3 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50"
              >
                Skills
              </Link>
            </div>
          </div>
        </div>
      )}

      <MyVisitsToday />

      <AIAssistantModal
        open={showDetailsModal}
        summary={summary}
        onClose={() => setShowDetailsModal(false)}
      />
    </div>
  );
};

export default TeacherHome;
